import React, { createContext, useState, useCallback, ReactNode, FC, useContext } from 'react';
import ConfirmDialog, { PromptField, PromptResult, PromptSelect } from './ConfirmDialog';

// Define the context type
export interface ConfirmDialogContextType {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: (value?: PromptResult) => void;
  onCancel: () => void;
  openConfirmDialog: (title: string, message: string, onConfirm: () => void) => void;
  openPromptDialog: (options: PromptDialogOptions) => void;
}

export interface PromptDialogOptions {
  title: string;
  message: string;
  field: PromptField;
  extraField?: PromptField;
  confirmLabel?: string;
  select?: PromptSelect;
  onConfirm: (value: PromptResult) => void;
}

// Create the context with default values
export const ConfirmDialogContext = createContext<ConfirmDialogContextType>({
  isOpen: false,
  title: '',
  message: '',
  onConfirm: () => { /* пустая функция по умолчанию */ },
  onCancel: () => { /* пустая функция по умолчанию */ },
  openConfirmDialog: () => { /* пустая функция по умолчанию */ },
  openPromptDialog: () => { /* пустая функция по умолчанию */ },
});

// Исправляем ошибку TypeScript, используя функцию-обертку для Provider
const ProviderWrapper: FC<{ value: ConfirmDialogContextType, children: ReactNode }> = ({ value, children }) => {
  return (
    <ConfirmDialogContext.Provider value={value}>
      {children}
    </ConfirmDialogContext.Provider>
  );
};

// Provider component
interface ConfirmDialogProviderProps {
  children: ReactNode;
}

export const ConfirmDialogProvider: FC<ConfirmDialogProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [promptOptions, setPromptOptions] = useState<Omit<PromptDialogOptions, 'title' | 'message' | 'onConfirm'> | undefined>();
  const [confirmAction, setConfirmAction] = useState<(value?: PromptResult) => void>(() => { /* пустая функция по умолчанию */ });

  const openConfirmDialog = useCallback((title: string, message: string, onConfirm: () => void) => {
    setTitle(title);
    setMessage(message);
    setPromptOptions(undefined);
    setConfirmAction(() => onConfirm);
    setIsOpen(true);
  }, []);

  const openPromptDialog = useCallback((options: PromptDialogOptions) => {
    const {title, message, onConfirm, ...prompt} = options;
    setTitle(title);
    setMessage(message);
    setPromptOptions(prompt);
    setConfirmAction(() => onConfirm);
    setIsOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    setPromptOptions(undefined);
  }, []);

  const handleConfirm = useCallback((value?: PromptResult) => {
    setIsOpen(false);
    setPromptOptions(undefined);
    confirmAction(value);
  }, [confirmAction]);

  return (
    <ProviderWrapper
      value={{
        isOpen,
        title,
        message,
        onConfirm: handleConfirm,
        onCancel: handleCancel,
        openConfirmDialog,
        openPromptDialog,
      }}
    >
      {children}
      <ConfirmDialog
        isOpen={isOpen}
        title={title}
        message={message}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        prompt={promptOptions}
      />
    </ProviderWrapper>
  );
};

// Hook to use the confirm dialog context
export const useConfirmDialog = () => useContext(ConfirmDialogContext);
